<template>
  <div class="container">
    <div class="left">
      <div class="video-container">
        <resize-observer @notify="handleResize" />
        <bg-eye-tracker
          v-if="totalTime && data"
          :key="eyeTrackerKey"
          :data="data"
          :currentTime="currentTime"
          :totalTime="totalTime"
          :testPlatform="testPlatform"
        />
        <bg-video-player
          :video="data.video"
          :getRef="setVideoRef"
          :currentTime="currentTime"
          :timeUpdate="timeUpdate"
          :newScrubberTime="newScrubberTime"
          :updateMetaData="handleVideoLoad"
          :customControls="customControls"
          :key="videoPlayerKey"
        />
      </div>
      <div class="chart-container">
        <bg-emotion-chart-labels
          v-if="videoRef && data"
          :data="data"
          :duration="videoRef.duration"
        />
        <bg-emotion-chart
          :key="chartKey"
          class="emotion-chart"
          :height="130"
          :data="data"
          :nueroFeedbackHover="handleNueroPop"
          :onGraphClick="setNewTime"
          @emotionPointClicked="selectPointById"
        />
        <bg-manual-markers
          v-if="videoRef && data"
          :points="manualPoints"
          :startedAt="data.started_at"
          :duration="videoRef.duration"
          :selectedId="selectedPoint ? selectedPoint.id : null"
          @select="selectPointById"
        />
        <bg-scrubber
          :key="scrubberKey"
          :scrubberTimeChange="setNewTime"
          v-if="videoRef"
          :currentTime="currentTime"
          :videoRef="videoRef"
          mouseDownTrigger
        />
        <bg-section :data="data" :currentTime="currentTime" class="section" />
        <div class="chart-toolbar">
          <bg-button
            v-if="isNeurofeedbackEditable"
            class="add-feedback"
            variant="secondary"
            text="Add feedback here"
            :disabled="!videoRef || !totalTime || isAdding"
            @click="addManualPoint"
          />
          <bg-legend class="legend" :showManual="true" />
        </div>
      </div>
    </div>
    <neurofeedback-interactive-sidebar
      v-if="isNeurofeedbackEditable && selectedPoint"
      :significantPointComment="selectedPoint.comment"
      :significantPointID="selectedPoint.id"
      :significantPointEmotion="selectedPoint.emotion"
      :significantPointFalsePositive="selectedPoint.false_positive"
      :significantPointTags="selectedPoint.tags"
      :deletable="true"
      @selectedPointUpdated="updateSelectedSignificantPoint"
      @change="assignInsight"
      @requestDelete="openDeleteModal"
      :tags="tags"
      :testId="testId"
      :testPlatform="testPlatform"
      :insightsOptions="insights"
    ></neurofeedback-interactive-sidebar>
    <div v-else class="neuro-container">
      <bg-nuerofeedback :data="data" :currentTime="currentTime" />
    </div>

    <bg-modal
      v-if="isDeleteModalOpen"
      :body="deleteModalBody"
      @close="closeDeleteModal"
    >
      <bg-loader :dark="true" v-if="isDeleting"></bg-loader>
      <bg-button variant="secondary" text="Cancel" @click="closeDeleteModal" />
      <bg-button
        variant="destructive"
        class="action"
        text="Delete"
        :disabled="isDeleting || deleteUsageCount === null"
        @click="confirmDelete"
      />
    </bg-modal>
  </div>
</template>

<style lang="scss" scoped>
@import "./styles.scss";
</style>

<script>
import moment from "moment";
import BgScrubber from "./bg-scrubber";
import BgVideoPlayer from "./bg-video-player";
import BgNuerofeedback from "./bg-neurofeedback";
import BgLegend from "../bg-legend";
import BgEyeTracker from "./bg-eye-tracker";
import BgEmotionChart from "./bg-emotion-chart";
import BgEmotionChartLabels from "./bg-emotion-chart/bg-emotion-chart-labels";
import BgManualMarkers from "./bg-emotion-chart/bg-manual-markers";
import BgSection from "./bg-section";
import BgButton from "@/components/bg-button";
import BgModal from "@/components/bg-modal";
import BgLoader from "@/components/bg-loader";
import NeurofeedbackInteractiveSidebar from "@/components/neurofeedback-interactive-sidebar";
import API from "@/api";

export default {
  name: "bg-video-chart",
  components: {
    BgVideoPlayer,
    BgEmotionChart,
    BgEyeTracker,
    BgScrubber,
    BgNuerofeedback,
    BgEmotionChartLabels,
    BgManualMarkers,
    BgLegend,
    BgSection,
    BgButton,
    BgModal,
    BgLoader,
    NeurofeedbackInteractiveSidebar,
  },
  props: {
    data: Object,
    customControls: {
      type: Boolean,
      default: false,
    },
    insights: Array,
    isNeurofeedbackEditable: {
      type: Boolean,
      default: false,
    },
    videoChartKey: {
      type: Number,
      optional: true,
    },
    tags: {
      type: Array,
      optional: true,
    },
    testId: {
      type: Number,
      optional: true,
    },
    testPlatform: {
      type: String,
      optional: true,
    },
  },
  watch: {
    videoChartKey(val) {
      this.videoPlayerKey = val;
    },
  },
  computed: {
    manualPoints() {
      return (this.data.significant_points || []).filter(
        (p) => p.emotion === "manual",
      );
    },
    deleteModalBody() {
      const isManual =
        this.selectedPoint && this.selectedPoint.emotion === "manual";
      let body = `Are you sure you want to delete this ${
        isManual ? "feedback marker" : "emotion peak"
      }?`;
      if (this.deleteUsageCount === null) {
        body += " Checking where it is used...";
      } else if (this.deleteUsageCount > 0) {
        const one = this.deleteUsageCount === 1;
        body += ` It is used by ${this.deleteUsageCount} insight${
          one ? "" : "s"
        } and will be removed from ${one ? "it" : "them"}.`;
      }
      return body;
    },
  },
  methods: {
    assignInsight(val) {
      const assign = {
        insightId: val.id,
        comment: this.selectedPoint.comment,
        emotion: this.selectedPoint.emotion,
        false_positive: this.selectedPoint.false_positive,
        id: this.selectedPoint.id,
        ranking: this.selectedPoint.ranking,
        score: this.selectedPoint.score,
        tag: this.selectedPoint.tag,
        tags: this.selectedPoint.tags,
        timestamp_top: this.selectedPoint.timestamp_top,
        pp_id: this.data.id,
      };
      this.$emit("change", assign);
    },
    setVideoRef(ref) {
      this.videoRef = ref;
    },
    timeUpdate(time) {
      this.currentTime = time;
    },
    setNewTime(newTimePercentage) {
      this.newScrubberTime = this.videoRef.duration * newTimePercentage;
    },
    selectPointById(id) {
      const point = (this.data.significant_points || []).find(
        (p) => p.id === id,
      );
      if (!point) return;
      this.selectedPoint = point;
      // A dot click already seeks through the canvas click handler; a marker
      // click does not reach the canvas, so seek explicitly.
      if (
        point.emotion === "manual" &&
        this.videoRef &&
        this.videoRef.duration
      ) {
        this.setNewTime(
          (point.timestamp_top - this.data.started_at) /
            1000 /
            this.videoRef.duration,
        );
      }
    },
    updateSelectedSignificantPoint(updatedFields) {
      if (updatedFields.comment)
        this.selectedPoint.comment = updatedFields.comment;
      if (updatedFields.false_positive)
        this.selectedPoint.false_positive = updatedFields.false_positive;
      if (updatedFields.tags) this.selectedPoint.tags = updatedFields.tags;
    },
    // Same conversion as the Sections tab (video seconds -> participant epoch ms),
    // clamped to the session because the video can be slightly longer.
    currentParticipantTime() {
      const timestamp =
        this.data.started_at + Math.floor(this.currentTime * 1000);
      return Math.min(
        Math.max(timestamp, this.data.started_at),
        this.data.ended_at,
      );
    },
    async addManualPoint() {
      if (this.isAdding || !this.videoRef) return;
      const timestamp = this.currentParticipantTime();
      const existing = this.manualPoints.find(
        (p) => p.timestamp_top === timestamp,
      );
      if (existing) {
        this.selectedPoint = existing;
        return;
      }
      this.isAdding = true;
      try {
        const point = await API.participantData.createSignificantPoint({
          participant_id: this.data.real_id,
          timestamp_top: timestamp,
        });
        if (!this.data.significant_points) {
          this.$set(this.data, "significant_points", []);
        }
        this.data.significant_points.push(point);
        this.data.significant_points.sort(
          (a, b) => a.timestamp_top - b.timestamp_top,
        );
        this.selectedPoint = point;
      } catch (error) {
        const errors =
          error.response && error.response.data && error.response.data.errors;
        alert(
          errors
            ? [].concat(errors).join("\n")
            : "Could not add the feedback marker.",
        );
      } finally {
        this.isAdding = false;
      }
    },
    openDeleteModal() {
      if (!this.selectedPoint) return;
      const id = this.selectedPoint.id;
      this.deleteUsageCount = null;
      this.isDeleteModalOpen = true;
      // Insight usage is only served live by the show endpoint (it is not part
      // of the cached participant payload), so look it up when the modal opens.
      API.participantData.getSignificantPoint(id).then((res) => {
        if (
          this.isDeleteModalOpen &&
          this.selectedPoint &&
          this.selectedPoint.id === id
        ) {
          this.deleteUsageCount = Number(res && res.insights_count) || 0;
        }
      });
    },
    closeDeleteModal() {
      if (this.isDeleting) return;
      this.isDeleteModalOpen = false;
    },
    async confirmDelete() {
      if (!this.selectedPoint || this.isDeleting) return;
      const point = this.selectedPoint;
      this.isDeleting = true;
      try {
        await API.participantData.deleteSignificantPoint(point.id);
        const list = this.data.significant_points || [];
        const index = list.findIndex((p) => p.id === point.id);
        if (index !== -1) list.splice(index, 1);
        this.selectedPoint = list[index] || list[index - 1] || null;
        // The chart builds its datasets once; remount it to drop a deleted dot.
        if (point.emotion !== "manual") this.chartKey += 1;
        this.isDeleteModalOpen = false;
        this.$emit("pointDeleted", {
          point,
          insightsCount: this.deleteUsageCount || 0,
        });
      } catch (error) {
        alert("Could not delete this point.");
      } finally {
        this.isDeleting = false;
      }
    },
    handleVideoLoad(video) {
      if (video) {
        if (this.$route.query.timestamp) {
          const startTime = moment(this.data.started_at);
          const endTime = moment(this.data.ended_at);
          const startPos = moment(Number(this.$route.query.timestamp));
          const videoLength = endTime.diff(startTime, "seconds");
          const timeElapsed = startPos.diff(startTime, "seconds");
          this.setNewTime(timeElapsed / videoLength);
        }
        this.totalTime = video.duration;
        // Inform parent components about the loaded video duration so they can
        // compare it with mouse-based time to complete and decide on UI hints
        this.$emit("videoLoaded", video.duration);
        this.eyeTrackerKey++;
      }
    },
    handleNueroPop(point) {
      if (this.nueroBubbles.find((nb) => (nb.id = point.id))) {
        return null;
      } else {
        this.nueroBubbles.push(point);
      }
    },
    handleResize() {
      this.eyeTrackerKey += 1;
      this.scrubberKey += 1;
    },
  },
  data() {
    return {
      videoRef: null,
      totalTime: 0,
      currentTime: 0,
      newScrubberTime: 0,
      nueroBubbles: [],
      eyeTrackerKey: 1,
      scrubberKey: 2,
      videoPlayerKey: 1000,
      chartKey: 1,
      isAdding: false,
      isDeleteModalOpen: false,
      deleteUsageCount: null,
      isDeleting: false,
      selectedPoint:
        this.data.significant_points && this.data.significant_points[0]
          ? this.data.significant_points[0]
          : null,
    };
  },
};
</script>
